import React, { useState, useMemo } from 'react'
import StudentListModal from './StudentListModal'
import { useTranslation } from 'react-i18next'
import { useHistory } from 'react-router-dom/cjs/react-router-dom.min'
import { studentPulseStats, pulseStatus, needsAttention } from '../../../constants/PulseCheckAnalyticsConstants'
import { ANALYTICS_EVENTS } from '../../../analytics/events'
import useTrackedSurface from '../../../analytics/useTrackedSurface'

function NeedsAttentionCard({ students = [] }) {
    const [modalOpen, setModalOpen] = useState(false)
    const { t } = useTranslation()
    const history = useHistory()
    useTrackedSurface(ANALYTICS_EVENTS.ANALYTICS_PULSE_NEEDS_ATTENTION_VIEWED, {
        active: students.length > 0,
        content: 'Needs Attention',
    })

    const list = useMemo(() => {
        return students
            .map(studentPulseStats)
            .filter(needsAttention)
            .sort((a, b) => (a.recent ?? 99) - (b.recent ?? 99))
            .slice(0, 4)
    }, [students])

    const openStudent = (id) => {
        if (id) history.push(`/analytics/student?id=${id}&focus=pulse`)
    }

    const initials = (name) =>
        (name || '')
            .split(' ')
            .map((n) => n[0])
            .join('')
            .slice(0, 2)

    return (
        <div className="w-[360px] flex-shrink-0 bg-white rounded-2xl px-[22px] py-[22px] border border-lightBG shadow-[0_12px_30px_rgba(45,43,69,0.06),0_1px_0_rgba(45,43,69,0.02)] flex flex-col gap-[14px]">
            {/* Header */}
            <div className="flex items-center justify-between">
                <div>
                    <div className="font-bold text-[16px] text-[#413F5E]">{t('Needs Attention')}</div>
                    <div className="font-medium text-[11px] text-[#9392AD]">{t('Students with low or declining pulse')}</div>
                </div>
                <span className="px-[10px] py-[4px] rounded-full bg-[#FDECEE] text-[#C73340] font-bold text-[11px]">{list.length}</span>
            </div>

            {/* List */}
            <div className="flex flex-col gap-[8px]">
                {list.length === 0 && <div className="py-6 text-center text-mudPurple font-medium text-[13px]">{t('No students need attention')}</div>}
                {list.map((s) => {
                    const status = pulseStatus(s)
                    return (
                        <div key={s.id || s.name} className="flex items-center gap-[12px] px-[12px] py-[10px] bg-containerGray border border-lightBG rounded-[12px]">
                            <span className="w-[34px] h-[34px] rounded-full bg-lightBG text-deepPurple font-bold text-[13px] flex items-center justify-center flex-shrink-0">
                                {initials(s.name)}
                            </span>
                            <div className="flex-1 min-w-0">
                                <div className="font-bold text-[13px] text-darkenPurple truncate">{s.name}</div>
                                <div className="font-medium text-[11px] text-mudPurple">
                                    {s.recent != null ? `${s.recent.toFixed(1)}/5` : t('No data')} · {s.delta >= 0 ? '+' : ''}
                                    {s.delta} {t('vs start')}
                                </div>
                            </div>
                            <div className="flex items-center gap-[6px] flex-shrink-0">
                                <span className="px-[9px] py-[3px] rounded-full font-bold text-[10px]" style={{ background: status.bg, color: status.fg }}>
                                    {t(status.label)}
                                </span>
                                <button
                                    title={t('Open student analytics')}
                                    onClick={() => openStudent(s.id)}
                                    disabled={!s.id}
                                    className="w-[28px] h-[28px] rounded-[8px] flex items-center justify-center flex-shrink-0 cursor-pointer bg-deepPurple text-white disabled:opacity-40"
                                >
                                    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
                                        <path d="M1 12s4-7 11-7 11 7 11 7-4 7-11 7S1 12 1 12z" />
                                        <circle cx="12" cy="12" r="3" />
                                    </svg>
                                </button>
                            </div>
                        </div>
                    )
                })}
            </div>

            {/* Button */}
            <button
                onClick={() => setModalOpen(true)}
                className="px-[14px] py-[10px] bg-deepPurple text-white rounded-full font-bold text-[13px] cursor-pointer shadow-[0_6px_18px_rgba(94,99,155,0.25)]"
            >
                {t('Open student list')}
            </button>

            <StudentListModal modalOpen={modalOpen} closeModal={() => setModalOpen(false)} students={students} />
        </div>
    )
}

export default NeedsAttentionCard
